import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { awardEmoji, awardNameKey, contextNameKey } from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import FreaksAvatar from './FreaksAvatar';
import { contextIcon } from './contextIcon';
import { FREAKS_GOLD, freaksSx as s } from './freaksStyles';

const MEDAL = ['🥇', '🥈', '🥉'];

export const eventText = (event, t) => {
  switch (event.type) {
    case 'challenge':
      return t(event.data.won ? 'theFreaks.events.challengeWon' : 'theFreaks.events.challengeLost', {
        target: event.targetIsBot ? `${event.target} 🤖` : `@${event.target}`,
      });
    case 'champion':
      return t('theFreaks.events.champion', { rank: event.data.rank, points: event.data.points });
    case 'base':
      return t('theFreaks.events.base', { name: event.data.name, level: event.data.level });
    case 'gear':
      return t('theFreaks.events.gear', { name: event.data.name });
    case 'award':
      return t(event.data.kind === 'medal' ? 'theFreaks.events.medal' : 'theFreaks.events.badge', {
        award: `${awardEmoji(event.data.key)} ${t(awardNameKey(event.data.key))}`,
        season: event.data.season ?? '',
      });
    case 'mission':
      return t('theFreaks.events.mission', { name: event.data.name });
    default:
      // an event type this app doesn't know yet: not shown
      return null;
  }
};

const timeAgo = (date) => {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(date).getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.round(minutes / 60)} h`;
};

// What's going on in Freakland: yesterday in the app, yesterday's champions and the latest game moves.
export default function FreaksToday() {
  const { t } = useFreaksT();
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setToday(await agent.TheFreaks.today());
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!today) {
    return failed
      ? <Typography sx={s.empty}>{t('theFreaks.errors.generic')}</Typography>
      : <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress sx={{ color: DEFAULT_THEME_COLOR }}/></Box>;
  }
  const { report, champions } = today;
  // leave out event types this app can't describe
  const events = today.events.filter((event) => eventText(event, t) !== null);

  return (
    <Box data-testid="freaks-today" sx={{ color: 'white' }}>
      <Box sx={s.card}>
        <Typography sx={s.title}>📰 {t('theFreaks.today.yesterday')}</Typography>
        <Box sx={{ display: 'flex', mt: 1.25 }}>
          {[
            { value: report.newFreaks, label: t('theFreaks.today.freaks') },
            { value: report.newReels, label: t('theFreaks.today.reels') },
            { value: report.newCatches, label: t('theFreaks.today.catches') },
          ].map(({ value, label }) => (
            <Box key={label} sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: DEFAULT_THEME_COLOR }}>{value}</Typography>
              <Typography noWrap sx={{ ...s.small, ...s.muted }}>{label}</Typography>
            </Box>
          ))}
        </Box>
        {report.busiestContexts.length ? (
          <Box sx={{ ...s.row, flexWrap: 'wrap', mt: 1, gap: 0.5 }}>
            <Typography sx={{ ...s.small, ...s.muted }}>{t('theFreaks.today.busiest')}:</Typography>
            {report.busiestContexts.map(({ context, freaks }) => (
              <Box key={context} sx={{ ...s.row, mr: 1.25, gap: 0.4 }}>
                {contextIcon(context, { fontSize: 14, color: DEFAULT_THEME_COLOR })}
                <Typography sx={s.small}>{t(contextNameKey(context))} ({freaks})</Typography>
              </Box>
            ))}
          </Box>
        ) : null}
        {report.topFreak ? (
          <ButtonBase
            data-testid="freaks-today-top-freak"
            onClick={() => navigate(`/freak/${report.topFreak.slug}`)}
            sx={{ display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', mt: 1.5, minHeight: 44, color: 'white', gap: 1 }}
          >
            <WhatshotIcon sx={{ color: FREAKS_GOLD }}/>
            <Box sx={s.grow}>
              <Typography sx={{ ...s.small, ...s.muted }}>{t('theFreaks.today.topFreak')}</Typography>
              <Typography noWrap sx={s.bold}>{report.topFreak.title}</Typography>
            </Box>
            <Typography sx={s.small}>❤️ {t('theFreaks.today.likes', { count: report.topFreak.likes })}</Typography>
          </ButtonBase>
        ) : null}
      </Box>

      {champions.length ? (
        <>
          <Typography sx={s.sectionTitle}>🏆 {t('theFreaks.today.champions')}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            {champions.map((champion) => (
              <Box key={champion.rank} sx={{ flex: 1, minWidth: 0, px: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 22 }}>{MEDAL[champion.rank - 1]}</Typography>
                <FreaksAvatar username={champion.username} imageUrl={champion.imageUrl}/>
                <Typography noWrap sx={{ ...s.small, ...s.bold, maxWidth: '100%' }}>@{champion.username}</Typography>
                <Typography sx={{ ...s.small, color: FREAKS_GOLD }}>{t('theFreaks.today.championPrize', { points: champion.points })}</Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : null}

      <Typography sx={s.sectionTitle}>⚡ {t('theFreaks.today.events')}</Typography>
      {!events.length ? <Typography sx={s.empty}>{t('theFreaks.today.noEvents')}</Typography> : null}
      {events.map((event) => (
        <Box key={event.id} sx={{ ...s.row, py: 0.75, gap: 1.25 }}>
          <FreaksAvatar username={event.username} imageUrl={event.imageUrl} size={32}/>
          <Typography sx={{ ...s.grow, fontSize: 14 }}>
            <Box component="span" sx={s.bold}>{event.isBot ? `${event.username} 🤖` : `@${event.username}`}</Box> {eventText(event, t)}
          </Typography>
          <Typography sx={{ ...s.small, ...s.muted, flexShrink: 0 }}>{timeAgo(event.createdAt)}</Typography>
        </Box>
      ))}
    </Box>
  );
}
